import api from "@/services/api";
import { FilterValues } from "@/components/ui/FilterBar/types";
import type { IncomeItem } from "@/services/financeiro";

/* ─── Tipos ──────────────────────────────────────────────────────────────── */

export interface ComercialUnitRow {
  id:           string;
  name:         string;
  enterpriseId: string;
  scopeLabel:   string;
  /** Centro de custo / obra (para filtro alinhado ao MultiSelect do Sienge). */
  costCenterId: string;
  /** Unidade construtiva (quadra/torre etc.) — vazio se a API não enviar. */
  constructiveUnitId: string;
  constructiveUnitName: string;
  /** Estoque comercial Sienge (`commercialStock` / situação), quando enviado. */
  commercialStock: string;
}

export interface ComercialData {
  /** Vendas (bulk-data sales, situation=SOLD) normalizadas para os gráficos */
  income: IncomeItem[];
  /**
   * Todas as vendas históricas no escopo de unidades selecionado.
   * Usado por indicadores cujo período é definido pelo ciclo completo de vendas.
   */
  salesHistory: IncomeItem[];
  /** Distratos (bulk-data sales, situation=CANCELED) no período, por cancellationDate */
  distratos: IncomeItem[];
  /**
   * Contratos cancelados com cancellationDate até a data da pesquisa (endDate).
   * Usado para anular vendas do cálculo sem alterar o card de distratos do período.
   */
  distratosAtePesquisa: IncomeItem[];
  /** Unidades imóveis do Sienge após o mesmo critério de centro de custo do Financeiro */
  units: ComercialUnitRow[];
}

/* ─── Helpers (campos Sienge variam entre ambientes) ───────────────────────── */

function getStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === "object") continue;
    const s = String(v).trim();
    if (s && s !== "0") return s;
  }
  return "";
}

/** Nome em objetos aninhados típicos do bulk-data `sales` (Sienge). */
const NESTED_CLIENT_ROOT_KEYS = [
  "customer",
  "client",
  "buyer",
  "purchaser",
  "holder",
  "businessPartner",
  "business_partner",
  "person",
  "debtor",
  "contractor",
  "financialCustomer",
  "financial_customer",
  "partner",
  "contratante",
  "comprador",
  "proprietary",
  "owner",
];

const NESTED_CLIENT_NAME_KEYS = [
  "name",
  "corporateName",
  "corporate_name",
  "tradeName",
  "trade_name",
  "tradingName",
  "legalName",
  "legal_name",
  "fullName",
  "full_name",
  "personName",
  "person_name",
  "customerName",
  "customer_name",
  "clientName",
  "client_name",
  "companyName",
  "company_name",
  "description",
  "razaoSocial",
  "razao_social",
  "nomeFantasia",
  "nome_fantasia",
  "fantasia",
  "label",
];

function nameFromNestedRecord(n: Record<string, unknown>): string {
  return getStr(n, ...NESTED_CLIENT_NAME_KEYS);
}

function extractClientNameFromSale(raw: Record<string, unknown>, depth = 0): string {
  if (depth > 2) return "";

  for (const root of NESTED_CLIENT_ROOT_KEYS) {
    const v = raw[root];
    if (v != null && typeof v === "object" && !Array.isArray(v)) {
      const nm = nameFromNestedRecord(v as Record<string, unknown>);
      if (nm) return nm;
    }
  }

  for (const arrKey of ["customers", "clients", "buyers", "partners", "holders"]) {
    const arr = raw[arrKey];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const first = arr[0];
    if (first != null && typeof first === "object" && !Array.isArray(first)) {
      const nm = nameFromNestedRecord(first as Record<string, unknown>);
      if (nm) return nm;
    }
  }

  for (const wrap of ["sale", "contract", "salesContract", "negotiation", "salesOrder"]) {
    const inner = raw[wrap];
    if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
      const sub = extractClientNameFromSale(inner as Record<string, unknown>, depth + 1);
      if (sub) return sub;
    }
  }

  return getStr(
    raw,
    "customerName",
    "customer_name",
    "clientName",
    "client_name",
    "buyerName",
    "buyer_name",
    "holderName",
    "holder_name",
    "businessPartnerName",
    "business_partner_name",
    "personName",
    "person_name",
    "companyName",
    "company_name",
    "purchaserName",
    "purchaser_name",
    "proprietaryName",
    "proprietary_name",
    "financialCustomerName",
    "financial_customer_name",
    "partnerName",
    "partner_name",
    "nomeCliente",
    "nome_cliente",
    "nomeComprador",
    "nome_comprador",
    "razaoSocialCliente",
    "razao_social_cliente",
    "name"
  );
}

function getNum(o: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = o[k];
    if (v != null) {
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
      if (!Number.isNaN(n)) return n;
    }
  }
  return 0;
}

/**
 * Mesma ideia do Gerencial: obtém id/nome de empreendimento ou centro na unidade.
 */
function extractCcFromUnit(u: Record<string, unknown>): { id: string; name: string } | null {
  const nestedKeys = ["enterprise", "empreendimento", "building", "costCenter", "cost_center"];
  for (const key of nestedKeys) {
    const nested = u[key];
    if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
      const n = nested as Record<string, unknown>;
      const id   = getStr(n, "id", "code", "costCenterId", "enterpriseId");
      const name = getStr(n, "name", "description", "costCenterName", "enterpriseName");
      if (id) return { id, name: name || id };
    }
  }

  const id = getStr(
    u,
    "enterpriseId", "enterprise_id",
    "costCenterId", "cost_center_id",
    "buildingId",   "building_id",
    "empreendimentoId"
  );
  const name = getStr(
    u,
    "enterpriseName", "enterprise_name",
    "costCenterName", "cost_center_name",
    "buildingName",   "building_name"
  );

  return id ? { id, name: name || id } : null;
}

/** Quadra / torre / UC — campos e aninhamentos comuns no bulk de unidades Sienge. */
function extractConstructiveUnitFromUnit(u: Record<string, unknown>): { id: string; name: string } | null {
  const nestedKeys = [
    "buildingUnit",
    "building_unit",
    "constructiveUnit",
    "constructive_unit",
    "constructionUnit",
    "construction_unit",
    "unidadeConstrutiva",
    "unidade_construtiva",
  ];
  for (const key of nestedKeys) {
    const nested = u[key];
    if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
      const n = nested as Record<string, unknown>;
      const id = getStr(
        n,
        "id",
        "buildingUnitId",
        "building_unit_id",
        "constructiveUnitId",
        "code"
      );
      const name = getStr(
        n,
        "name",
        "description",
        "buildingUnitName",
        "building_unit_name",
        "constructiveUnitName"
      );
      if (id) return { id, name: name || id };
    }
  }

  const id = getStr(
    u,
    "buildingUnitId",
    "building_unit_id",
    "constructiveUnitId",
    "constructive_unit_id",
    "constructionUnitId",
    "construction_unit_id",
    "realEstateBuildingUnitId"
  );
  const name = getStr(
    u,
    "buildingUnitName",
    "building_unit_name",
    "constructiveUnitName",
    "constructive_unit_name",
    "constructionUnitName",
    "buildingUnitDescription",
    "building_unit_description"
  );
  if (id) return { id, name: name || id };
  if (name) return { id: name, name };
  return null;
}

function extractUnitLabel(u: Record<string, unknown>): string {
  return getStr(
    u,
    "description", "name", "unitDescription",
    "commercialDescription", "blockUnit",
    "code"
  ) || "—";
}

function normalizeUnitRow(raw: Record<string, unknown>): ComercialUnitRow | null {
  const cc = extractCcFromUnit(raw);
  const label = extractUnitLabel(raw);
  let id = getStr(raw, "id", "unitId", "code", "commercialId", "commercial_id");
  /** Sienge às vezes omite id; mantém a unidade se houver obra + descrição (igual ao agrupamento Gerencial). */
  if (!id && cc?.id && label && label !== "—") {
    const slug = label
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .replace(/[^\w]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 64);
    id = slug ? `${cc.id}:${slug}` : `${cc.id}:u`;
  }
  if (!id) return null;
  const enterpriseId = getStr(raw, "enterpriseId", "enterprise_id") || cc?.id || "";
  const costCenterId = (
    cc?.id ||
    getStr(raw, "costCenterId", "cost_center_id") ||
    enterpriseId
  ).trim();
  if (!costCenterId) return null;
  const uc = extractConstructiveUnitFromUnit(raw);
  return {
    id,
    name: extractUnitLabel(raw),
    enterpriseId: enterpriseId || id,
    scopeLabel: cc?.name ?? enterpriseId ?? "—",
    costCenterId: costCenterId || id,
    constructiveUnitId: uc?.id ?? "",
    constructiveUnitName: uc?.name ?? "",
    commercialStock: getStr(
      raw,
      "commercialStock",
      "commercial_stock",
      "estoqueComercial",
      "estoque_comercial"
    ),
  };
}

/** Critério alinhado ao Financeiro: CC vazio usa allCcIds; senão só CCs selecionados. */
function unitMatchesCostFilter(
  u: Record<string, unknown>,
  filters: FilterValues,
  allCcIds?: string[]
): boolean {
  const selected = (filters.costCenter ?? []).map((x) => String(x).trim()).filter(Boolean);
  const cc = extractCcFromUnit(u);
  const ccId = cc?.id ? String(cc.id).trim() : "";

  if (selected.length > 0) {
    if (!ccId) return false;
    return selected.includes(ccId);
  }

  if (allCcIds && allCcIds.length > 0) {
    if (!ccId) return false;
    return allCcIds.includes(ccId);
  }

  return true;
}

async function fetchUnitsRaw(signal?: AbortSignal): Promise<unknown[]> {
  /* Listagem completa no Sienge pode levar vários minutos (paginação + retries). */
  const { data } = await api.get("/api/units", { signal, timeout: 180_000 });
  const list: unknown[] = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.results)
      ? data.results
      : [];
  return list;
}

async function fetchAllEnterpriseIds(signal?: AbortSignal): Promise<string[]> {
  const { data } = await api.get("/api/enterprises", {
    params: { companyId: "1", type: "1" },
    signal,
  });
  const list: unknown[] = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.results)
      ? data.results
      : [];
  const ids = new Set<string>();
  for (const row of list) {
    if (!row || typeof row !== "object") continue;
    const o = row as Record<string, unknown>;
    const id = getStr(o, "id", "enterpriseId", "code");
    if (id) ids.add(id);
  }
  return [...ids];
}

function enterpriseIdsFromUnits(filteredUnits: Record<string, unknown>[]): string[] {
  const set = new Set<string>();
  for (const u of filteredUnits) {
    const direct = getStr(u, "enterpriseId", "enterprise_id");
    if (direct) {
      set.add(direct);
      continue;
    }
    const cc = extractCcFromUnit(u);
    if (cc?.id) set.add(String(cc.id).trim());
  }
  return [...set];
}

async function resolveEnterpriseIdsForSales(
  filteredUnits: Record<string, unknown>[],
  filters: FilterValues,
  signal?: AbortSignal
): Promise<string[]> {
  let ids = enterpriseIdsFromUnits(filteredUnits);
  if (ids.length > 0) return ids;

  const selected = (filters.costCenter ?? []).map((x) => String(x).trim()).filter(Boolean);
  if (selected.length > 0) return selected;

  return fetchAllEnterpriseIds(signal);
}

function extractBrokerNameFromSale(raw: Record<string, unknown>, depth = 0): string {
  if (depth > 2) return "";

  for (const key of ["brokers", "BROKERS", "Brokers", "broker", "BROKER"]) {
    const v = raw[key];
    if (Array.isArray(v) && v.length > 0) {
      const records = v.filter(
        (x): x is Record<string, unknown> => x != null && typeof x === "object" && !Array.isArray(x),
      );
      const main = records.find((o) => o.main === true || o.Main === true || o.isMain === true);
      const ordered = main ? [main, ...records.filter((o) => o !== main)] : records;
      for (const o of ordered) {
        const nm = getStr(o, "name", "Name", "NAME", "brokerName", "corporateName", "tradeName");
        if (nm) return nm;
      }
    }
    if (v != null && typeof v === "object" && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      const nm = getStr(o, "name", "Name", "NAME", "brokerName", "corporateName", "tradeName");
      if (nm) return nm;
    }
  }

  for (const wrap of ["sale", "contract", "salesContract", "negotiation", "salesOrder"]) {
    const inner = raw[wrap];
    if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
      const sub = extractBrokerNameFromSale(inner as Record<string, unknown>, depth + 1);
      if (sub) return sub;
    }
  }

  return getStr(
    raw,
    "brokerName",
    "broker_name",
    "realEstateAgency",
    "agencyName",
    "imobiliaria",
    "vendedor",
  );
}

/** Condições excluídas do cálculo pré-chave: PP, PF e DE (DESCONTO). */
const POS_CHAVE_CONDITION_TYPES = new Set(["PP", "PF", "DE"]);

function sumPaymentConditions(
  raw: Record<string, unknown>,
  shouldInclude: (conditionType: string) => boolean,
): number {
  const pcs = raw.paymentConditions ?? raw.payment_conditions;
  if (!Array.isArray(pcs)) return 0;
  let sum = 0;
  for (const pc of pcs) {
    if (pc == null || typeof pc !== "object" || Array.isArray(pc)) continue;
    const o = pc as Record<string, unknown>;
    const typeId = getStr(o, "conditionTypeId", "condition_type_id").toUpperCase();
    if (!shouldInclude(typeId)) continue;
    sum += getNum(o, "totalValue", "total_value");
  }
  return sum;
}

/** Soma `paymentConditions[].totalValue` das condições que NÃO são PP/PF/DE (valor vendido até a entrega da chave). */
function extractPreChaveAmount(raw: Record<string, unknown>): number {
  return sumPaymentConditions(raw, (typeId) => !POS_CHAVE_CONDITION_TYPES.has(typeId));
}

/** Soma o desconto das condições DE para removê-lo do preço médio geral. */
function extractDiscountAmount(raw: Record<string, unknown>): number {
  return sumPaymentConditions(raw, (typeId) => typeId === "DE");
}

function normalizeSaleToIncomeItem(
  raw: Record<string, unknown>,
  idx: number,
  enterpriseId?: string
): IncomeItem | null {
  const billId =
    getStr(raw, "id", "saleId", "contractId", "documentId", "contractNumber") || `sale-${idx}`;
  const originalAmount = getNum(
    raw,
    "totalSellingValue",
    "totalSellingPrice",
    "totalValue",
    "saleValue",
    "amount",
    "contractValue",
    "originalAmount",
    "value",
    "totalAmount",
    "negotiatedValue"
  );
  if (originalAmount <= 0) return null;

  const clientName = extractClientNameFromSale(raw);
  const brokerName = extractBrokerNameFromSale(raw);

  const rawDate =
    getStr(
      raw,
      "contractDate",
      "saleDate",
      "issueDate",
      "createdDate",
      "createdAt",
      "date",
      "saleOrderDate"
    ) || "";
  const issueDate = rawDate.length >= 10 ? rawDate.substring(0, 10) : "";
  const dueDateRaw = getStr(raw, "dueDate", "deliveryDate", "forecastDate", "expectedDeliveryDate");
  const dueDate = (dueDateRaw.length >= 10 ? dueDateRaw.substring(0, 10) : "") || issueDate;

  const balanceAmount = getNum(raw, "balanceAmount", "openAmount", "remainingBalance", "balance", "openBalance");
  const paidAmount = getNum(raw, "paidAmount", "receivedAmount", "totalPaid", "entryAmount");

  let isPaid = false;
  if (balanceAmount > 0.01) {
    isPaid = false;
  } else if (paidAmount > 0.01 && paidAmount >= originalAmount - 0.02) {
    isPaid = true;
  } else if (balanceAmount <= 0.01 && getStr(raw, "balanceAmount", "openAmount") === "") {
    /* Sem campo de saldo: venda concretizada conta como realizada no período comercial */
    isPaid = true;
  } else {
    isPaid = balanceAmount <= 0.01;
  }

  const documentType = getStr(raw, "documentType", "type", "situation", "saleType", "modality") || "Venda";

  const buildingUnitId = getStr(
    raw,
    "unitId",
    "buildingUnitId",
    "unit_id",
    "constructionUnitId",
    "realEstateUnitId"
  );
  const buildingUnitName = getStr(
    raw,
    "unitName",
    "unitDescription",
    "buildingUnitName",
    "commercialUnit"
  );

  const payRaw = getStr(raw, "paymentDate", "lastPaymentDate");
  const paymentDate = (payRaw.length >= 10 ? payRaw.substring(0, 10) : "") || (isPaid ? issueDate : "");

  const cancelRaw = getStr(raw, "cancellationDate", "cancelDate", "distratoDate");
  const cancellationDate = cancelRaw.length >= 10 ? cancelRaw.substring(0, 10) : undefined;
  const cancellationReason = getStr(raw, "cancellationReason", "cancelReason", "distratoReason") || undefined;

  const nomeCliente =
    clientName && clientName !== "—" && clientName !== "-" && clientName !== "–" ? clientName : "";
  const displayClient =
    nomeCliente ||
    buildingUnitName ||
    getStr(raw, "description", "notes", "remarks") ||
    "";

  const ccFromUnit = extractCcFromUnit(raw);
  const costCenterId = (
    enterpriseId?.trim() ||
    ccFromUnit?.id ||
    getStr(raw, "enterpriseId", "enterprise_id", "costCenterId", "cost_center_id", "buildingId")
  ).trim() || undefined;
  const costCenterName = (
    ccFromUnit?.name ||
    getStr(raw, "enterpriseName", "enterprise_name", "costCenterName", "cost_center_name", "buildingName")
  ).trim() || undefined;

  return {
    billId,
    issueDate:           issueDate || dueDate,
    dueDate:             dueDate || issueDate,
    paymentDate,
    originalAmount,
    balanceAmount:       isPaid ? 0 : Math.max(0, balanceAmount),
    isPaid,
    clientName:          displayClient || "Cliente não informado",
    documentType,
    description:         getStr(raw, "description", "notes", "remarks") || documentType,
    buildingUnitId:    buildingUnitId || undefined,
    buildingUnitName:  buildingUnitName || undefined,
    costCenterId,
    costCenterName,
    brokerName: brokerName || undefined,
    discountAmount: extractDiscountAmount(raw) || undefined,
    preChaveAmount: extractPreChaveAmount(raw) || undefined,
    cancellationDate,
    cancellationReason,
  };
}

const SALES_CONCURRENCY = 4;

async function mapPool<T, R>(
  items: T[],
  mapper: (item: T) => Promise<R[]>,
  concurrency: number
): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += concurrency) {
    const chunk = items.slice(i, i + concurrency);
    const parts = await Promise.all(chunk.map(mapper));
    for (const p of parts) out.push(...p);
  }
  return out;
}

/**
 * Vendas por empreendimento (Sienge bulk-data sales).
 * @param situation SOLD (vendas) ou CANCELED (distratos)
 */
async function fetchSalesForEnterprise(
  enterpriseId: string,
  createdAfter: string,
  createdBefore: string,
  signal?: AbortSignal,
  situation: "SOLD" | "CANCELED" = "SOLD"
): Promise<IncomeItem[]> {
  const { data } = await api.get("/api/sales", {
    params: {
      enterpriseId,
      createdAfter,
      createdBefore,
      situation,
    },
    signal,
  });
  const list: unknown[] = Array.isArray(data?.data) ? data.data : [];
  return list
    .map((row, i) => normalizeSaleToIncomeItem(row as Record<string, unknown>, i, enterpriseId))
    .filter((x): x is IncomeItem => x !== null);
}

function dedupeIncomeByBill(items: IncomeItem[]): IncomeItem[] {
  const seen = new Set<string>();
  const out: IncomeItem[] = [];
  for (const it of items) {
    const k = `${it.billId}|${it.originalAmount}|${it.issueDate}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}

/** Janela ampla de criação para não perder contratos antigos distrados no período. */
const DISTRATOS_CREATED_AFTER = "2010-01-01";
/** Janela histórica para indicadores baseados no ciclo completo de vendas. */
const SALES_HISTORY_CREATED_AFTER = "2010-01-01";

function getCurrentLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function filterByUnitScope(items: IncomeItem[], filteredUnits: Record<string, unknown>[]): IncomeItem[] {
  if (filteredUnits.length === 0) return items;
  const unitIds = new Set<string>();
  for (const u of filteredUnits) {
    const id = getStr(u, "id", "unitId", "code");
    if (id) unitIds.add(id);
  }
  if (unitIds.size === 0) return items;
  return items.filter((item) => {
    const uid = (item.buildingUnitId ?? "").trim();
    if (!uid) return true;
    return unitIds.has(uid);
  });
}

/**
 * Carrega unidades, vendas históricas e o recorte de vendas do período,
 * respeitando centros de custo como no Financeiro.
 */
export async function fetchComercialData(
  filters: FilterValues,
  allCcIds?: string[],
  signal?: AbortSignal
): Promise<ComercialData> {
  const [rawUnits] = await Promise.all([fetchUnitsRaw(signal)]);

  const unitRecords: Record<string, unknown>[] = rawUnits.filter(
    (u): u is Record<string, unknown> =>
      u != null && typeof u === "object" && !Array.isArray(u)
  );

  const filteredUnits = unitRecords.filter((u) => unitMatchesCostFilter(u, filters, allCcIds));

  const units: ComercialUnitRow[] = filteredUnits
    .map((u) => normalizeUnitRow(u))
    .filter((x): x is ComercialUnitRow => x !== null);

  const enterpriseIds = await resolveEnterpriseIdsForSales(filteredUnits, filters, signal);

  const salesHistoryEndDate = getCurrentLocalDate();

  const [allSales, allCanceled] = await Promise.all([
    mapPool(
      enterpriseIds,
      (eid) => fetchSalesForEnterprise(
        eid,
        SALES_HISTORY_CREATED_AFTER,
        salesHistoryEndDate,
        signal,
        "SOLD"
      ),
      SALES_CONCURRENCY
    ),
    mapPool(
      enterpriseIds,
      (eid) =>
        fetchSalesForEnterprise(eid, DISTRATOS_CREATED_AFTER, salesHistoryEndDate, signal, "CANCELED"),
      SALES_CONCURRENCY
    ),
  ]);

  let salesHistory = dedupeIncomeByBill(allSales);
  salesHistory = filterByUnitScope(salesHistory, filteredUnits);

  const income = salesHistory.filter((item) => {
    const d = item.issueDate || item.dueDate;
    if (!d) return true;
    return d >= filters.startDate && d <= filters.endDate;
  });

  let distratosAll = dedupeIncomeByBill(allCanceled);
  distratosAll = filterByUnitScope(distratosAll, filteredUnits);

  const distratosAtePesquisa = distratosAll.filter((item) => {
    const d = item.cancellationDate;
    if (!d) return false;
    return d <= filters.endDate;
  });

  const distratos = distratosAtePesquisa.filter((item) => {
    const d = item.cancellationDate;
    if (!d) return false;
    return d >= filters.startDate;
  });

  return { income, salesHistory, distratos, distratosAtePesquisa, units };
}

/**
 * Todas as unidades (cadastro Sienge) para módulos que não aplicam filtro de CC — ex.: Chão de Obra.
 */
export async function fetchAllUnitsForRegistry(signal?: AbortSignal): Promise<ComercialUnitRow[]> {
  const rawUnits = await fetchUnitsRaw(signal);
  const unitRecords: Record<string, unknown>[] = rawUnits.filter(
    (u): u is Record<string, unknown> =>
      u != null && typeof u === "object" && !Array.isArray(u)
  );
  return unitRecords
    .map((u) => normalizeUnitRow(u))
    .filter((x): x is ComercialUnitRow => x !== null);
}
